import { formatMoney } from '@mt/shared';
import { RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/states';
import { CHANNEL_LABELS, useRunSummary, useSummaries, type DailySummary, type SummaryNv } from './api';

/**
 * Tóm tắt công việc hằng ngày (CR-014 bước 7b, DEC-233). Bản cuối ngày đến bằng thông báo; NV bấm "Tóm tắt ngay" để xem số liệu
 * tới lúc bấm. TP / GĐ thấy thêm bảng NV trong phòng / cả công ty. Bấm khách đang chờ → mở hội thoại.
 */
const fmtDate = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}`;
const fmtSince = (iso: string) => {
  const d = new Date(iso);
  const today = new Date().toDateString() === d.toDateString();
  return today ? d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' }) : d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' });
};

export function SummaryDialog({ open, onClose, onOpenConversation }: { open: boolean; onClose: () => void; onOpenConversation: (id: number) => void }) {
  const list = useSummaries(open);
  const run = useRunSummary();
  const [id, setId] = useState<number | null>(null);
  const items = list.data ?? [];
  const cur = items.find((s) => s.id === id) ?? items[0] ?? null;
  useEffect(() => {
    if (!open) setId(null);
  }, [open]);

  const refresh = () =>
    run.mutate(undefined, {
      onSuccess: (s) => setId(s.id),
      onError: (e) => toast.error(e instanceof Error ? e.message : 'Có lỗi, thử lại sau'),
    });

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => !v && onClose()}
      title="Tóm tắt công việc"
      description="Khách nhắn, khách còn chờ, báo giá, follow-up trong ngày. Bản cuối ngày gửi tự động vào thông báo."
      className="max-w-2xl"
      footer={
        <>
          {items.length > 1 && (
            <select
              className="mr-auto h-8 rounded-md border bg-background px-2 text-sm"
              value={cur?.id ?? ''}
              onChange={(e) => setId(Number(e.target.value))}
              aria-label="Chọn ngày"
            >
              {items.map((s) => (
                <option key={s.id} value={s.id}>
                  Ngày {fmtDate(s.date)}
                </option>
              ))}
            </select>
          )}
          <Button size="sm" onClick={refresh} disabled={run.isPending}>
            <RefreshCw className={run.isPending ? 'animate-spin' : undefined} /> Tóm tắt ngay
          </Button>
        </>
      }
    >
      {list.isLoading ? (
        <Spinner />
      ) : !cur ? (
        <p className="text-sm text-muted-foreground">Chưa có bản tóm tắt nào. Bấm “Tóm tắt ngay” để xem số liệu hôm nay.</p>
      ) : (
        <SummaryView s={cur} onOpenConversation={onOpenConversation} />
      )}
    </Dialog>
  );
}

function SummaryView({ s, onOpenConversation }: { s: DailySummary; onOpenConversation: (id: number) => void }) {
  const me = s.stats.self;
  return (
    <div className="space-y-4 text-sm">
      <div className="text-xs text-muted-foreground">
        Ngày {fmtDate(s.date)} · cập nhật {new Date(s.updatedAt).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Kpi label="Khách nhắn" value={me.customers} sub={`${me.messagesIn} tin`} />
        <Kpi label="Đã trả lời" value={me.replied} sub={`${me.messagesOut} tin`} />
        <Kpi label="Khách đang chờ" value={me.waitingCount} warn={me.waitingCount > 0} />
        <Kpi label="Báo giá lập" value={me.quotesCreated} sub={me.quotesCreated ? `${formatMoney(me.quotesValue)} đ` : `gửi qua hộp thư ${me.quotesSent}`} />
      </div>
      {(me.followupsDue > 0 || me.followupsTomorrow > 0 || me.botDrafts > 0) && (
        <p className="text-xs text-muted-foreground">
          Follow-up: {me.followupsDue} đến hạn / quá hạn, {me.followupsTomorrow} đến hạn mai
          {me.botDrafts > 0 && ` · ${me.botDrafts} nháp trợ lý chờ gửi`}
        </p>
      )}
      {me.waiting.length > 0 && (
        <div>
          <div className="mb-1 font-medium">Khách đang chờ trả lời</div>
          <ul className="divide-y rounded-md border">
            {me.waiting.map((w) => (
              <li key={w.conversationId}>
                <button
                  type="button"
                  onClick={() => onOpenConversation(w.conversationId)}
                  className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left hover:bg-muted"
                >
                  <span className="truncate">{w.name}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {CHANNEL_LABELS[w.channel] ?? w.channel} · chờ từ {fmtSince(w.since)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {(s.notes.comment || s.notes.tomorrow.length > 0) && (
        <div className="rounded-md bg-muted/50 p-3">
          {s.notes.comment && <p className="mb-2">{s.notes.comment}</p>}
          {s.notes.tomorrow.length > 0 && (
            <>
              <div className="font-medium">Việc nên làm ngày mai</div>
              <ul className="ml-4 list-disc">
                {s.notes.tomorrow.map((t, i) => (
                  <li key={i}>{t}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
      {s.stats.team && <TeamTable title={s.scope === 'company' ? 'Cả công ty' : 'Nhân viên trong phòng'} team={s.stats.team} />}
    </div>
  );
}

function Kpi({ label, value, sub, warn }: { label: string; value: number; sub?: string; warn?: boolean }) {
  return (
    <div className="rounded-md border p-2">
      <div className="text-[11px] text-muted-foreground">{label}</div>
      <div className={warn ? 'text-lg font-semibold text-error' : 'text-lg font-semibold'}>{value}</div>
      {sub && <div className="truncate text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

function TeamTable({ title, team }: { title: string; team: SummaryNv[] }) {
  if (!team.length) return <p className="text-xs text-muted-foreground">{title}: chưa có nhân viên nào có việc trong ngày.</p>;
  return (
    <div>
      <div className="mb-1 font-medium">{title}</div>
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-xs">
          <thead className="bg-muted/50 text-muted-foreground">
            <tr>
              <th className="px-2 py-1.5 text-left font-medium">Nhân viên</th>
              <th className="px-2 py-1.5 text-right font-medium">Khách nhắn</th>
              <th className="px-2 py-1.5 text-right font-medium">Đã trả lời</th>
              <th className="px-2 py-1.5 text-right font-medium">Đang chờ</th>
              <th className="px-2 py-1.5 text-right font-medium">Báo giá</th>
              <th className="px-2 py-1.5 text-right font-medium">Follow-up đến hạn</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {team.map((t) => (
              <tr key={t.userId}>
                <td className="px-2 py-1.5">{t.name}</td>
                <td className="px-2 py-1.5 text-right">{t.customers}</td>
                <td className="px-2 py-1.5 text-right">{t.replied}</td>
                <td className={t.waitingCount ? 'px-2 py-1.5 text-right font-semibold text-error' : 'px-2 py-1.5 text-right'}>{t.waitingCount}</td>
                <td className="px-2 py-1.5 text-right">{t.quotesCreated}</td>
                <td className="px-2 py-1.5 text-right">{t.followupsDue}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
